/**
 * Dave deal card body (AGS-869/949/950): a deal's issue or reminder, what
 * Dave did on it (registered it, changed its price, cancelled it),
 * or a registration Dave couldn't complete. Who registered the
 * deal, the card's sentences, a "Why?" tooltip when the card has numbers
 * behind it, the deal line, and the deal's earlier updates (see
 * daveDealGroups). Text only in V1: the Accept new price / Keep deal / Cancel
 * deal buttons come in V1.1.
 */
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import React, { useState } from "react";

import { AGENT_INFO } from "../../../constants";

import DaveDealLine from "./DaveDealLine";
import {
  LABEL_ALERT,
  LABEL_DECISION,
  LABEL_DONE,
  LABEL_IN_PROGRESS,
  getDaveCardView,
} from "./daveCardCopy";

const LABEL_COLORS = {
  [LABEL_DECISION]: { backgroundColor: "#fff3e0", color: "#e65100" },
  [LABEL_ALERT]: { backgroundColor: "#ffebee", color: "#c62828" },
  [LABEL_DONE]: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
  [LABEL_IN_PROGRESS]: { backgroundColor: "#e3f2fd", color: "#1565c0" },
};
const FYI_COLORS = { backgroundColor: "#f5f5f5", color: "#616161" };

export const DaveLabelChip = ({ label }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      height: 20,
      fontSize: "0.65rem",
      fontWeight: 700,
      letterSpacing: "0.04em",
      ...(LABEL_COLORS[label] || FYI_COLORS),
    }}
  />
);

export const earlierUpdatesText = (count) =>
  `${count} earlier update${count === 1 ? "" : "s"}`;

const formatDay = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    : "";

// The deal's older events, newest first: one line each, collapsed by default
const DaveEarlierUpdates = ({ earlier, marketplace, now }) => {
  const [open, setOpen] = useState(false);
  const items = earlier
    .map((d) => ({ d, view: getDaveCardView(d, { marketplace, now }) }))
    .filter(({ view }) => view);
  if (items.length === 0) return null;

  return (
    <Box
      data-testid="dave-earlier-updates"
      sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #e0e0e0" }}
    >
      <Box
        role="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          cursor: "pointer",
          color: "#888",
          "&:hover": { color: "#555" },
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {earlierUpdatesText(items.length)}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 16,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      {open && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          {items.map(({ d, view }) => (
            <Box
              key={d.card_id ?? d.execution_id}
              data-testid="dave-earlier-update"
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#bbb", whiteSpace: "nowrap" }}
                >
                  {formatDay(d.created_at)}
                </Typography>
                <DaveLabelChip label={view.label} />
                <Typography
                  variant="caption"
                  sx={{ color: "#333", fontWeight: 600 }}
                >
                  {view.title}
                </Typography>
              </Box>
              {view.lines[0] && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#666", mt: 0.25 }}
                >
                  {view.lines[0]}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const DaveMonitorCard = ({ daveData, marketplace, now }) => {
  const view = getDaveCardView(daveData, { marketplace, now });
  if (!view) return null;

  return (
    <Box
      data-testid="dave-monitor-card"
      sx={{
        border: "1px solid #e0e0e0",
        borderLeft: `3px solid ${AGENT_INFO.dave.color}`,
        borderRadius: 1,
        p: 1.5,
        backgroundColor: "#fff",
      }}
    >
      {/* The label chip sits in the accordion header, next to Dave's name */}
      {view.registeredBy && (
        <Typography
          variant="caption"
          sx={{ display: "block", color: "#888", mb: 0.5 }}
        >
          {view.registeredBy}
        </Typography>
      )}

      {view.prefix && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 600,
            color: AGENT_INFO.dave.color,
          }}
        >
          {view.prefix}
        </Typography>
      )}
      {view.lines.map((line) => (
        <Typography key={line} variant="body2" sx={{ color: "#333", mb: 0.5 }}>
          {line}
        </Typography>
      ))}

      {(view.note || view.why.length > 0) && (
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
          {view.note && (
            <Typography
              variant="caption"
              sx={{ color: "#aaa", fontStyle: "italic" }}
            >
              {view.note}
            </Typography>
          )}
          {view.why.length > 0 && (
            <Tooltip
              arrow
              title={view.why.map((line) => (
                <div key={line}>{line}</div>
              ))}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#999",
                  cursor: "help",
                  textDecoration: "underline",
                  "&:hover": { color: "#666" },
                }}
              >
                Why?
              </Typography>
            </Tooltip>
          )}
        </Box>
      )}

      <DaveDealLine daveData={daveData} marketplace={marketplace} now={now} />

      {daveData.daveEarlier?.length > 0 && (
        <DaveEarlierUpdates
          earlier={daveData.daveEarlier}
          marketplace={marketplace}
          now={now}
        />
      )}
    </Box>
  );
};

export default DaveMonitorCard;
